import { useDispatch, useSelector } from 'react-redux';

import { Controller, useForm } from 'react-hook-form';

import { useExecutePaymentMutation } from 'api/balance-api';
import { NOTIFICATIONS } from 'constants/notifications';
import { Button, ButtonColor, Input, Loader, UserInformationMissingModal } from 'shared';
import RootModalLayout from 'shared/Modals/RootModalLayout';
import { ModalProps } from 'types/components/modal.types';
import { POSITIVE_AMOUNT_REGEX } from 'utils/validators';

import { ReactComponent as EuroIcon } from 'assets/icons/EuroIcon.svg';
import { ReactComponent as USDIcon } from 'assets/icons/USDIcon.svg';

import { Box, FormControl, Radio, RadioGroup, Typography } from '@mui/material';
import { PAYMENT_OPTIONS } from 'constants/general';
import { AppDispatch } from 'store';
import { showErrorNotification, showSuccessNotification } from 'store/slices/notification-slice';
import { userDataSelector } from 'store/slices/user-slice';
import { useTheme } from 'themes/themeProvider';
import { Currency, ThemeKey } from 'types/general.types';
import { PaymentOption, PaymentType } from 'types/payment.types';
import classes from './ModalTopUp.module.scss';

interface FormValues {
  amount: number;
  paymentType: PaymentType;
}

const themePaymentSupport: Partial<Record<ThemeKey, PaymentOption['value'][]>> = {
  [ThemeKey.CRYPTOHALL24_DUERR]: ['crypto', 'fiat', 'klarna'],
  [ThemeKey.CRYPTOHALL24_QUETTA]: ['crypto'],
  [ThemeKey.AMITYAGE]: ['crypto', 'fiat'],
  [ThemeKey.PANTHEON]: ['crypto', 'fiat'],
  [ThemeKey.PANTHEON_AUS]: ['crypto'],
  [ThemeKey.AVATRON]: ['fiat', 'crypto'],
  [ThemeKey.PAYMINE]: ['fiat', 'klarna'],
};

export const ModalTopUp = ({ onClose, testId = 'modal-top-up' }: ModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { themeKey } = useTheme();
  const user = useSelector(userDataSelector);

  const isUSDCompany = user?.baseCurrency === Currency.USD;

  const supportedPayments = themePaymentSupport[themeKey] || ['crypto', 'fiat', 'klarna'];
  // Stripe top-up is available to all prepaid customers regardless of theme — no feature-flag
  // gate, so it isn't part of the per-theme allow-list above (unlike crypto/fiat/klarna).
  const filteredOptions = PAYMENT_OPTIONS.filter(
    option => supportedPayments.includes(option.value) || option.value === 'stripe',
  );

  const [executePayment, { isLoading: isPaymentExecuting }] = useExecutePaymentMutation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      paymentType: filteredOptions[0]?.value ?? 'crypto',
    },
  });

  const additionalPaymentInfo = (
    <Box>
      <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
        PLEASE NOTE:
      </Typography>

      <Typography variant='body2'>
        <strong>Crypto payments:</strong> 0% fee
      </Typography>
      <Typography variant='body2'>
        <strong>Bank payments:</strong> 0% fee
      </Typography>
      {themeKey === ThemeKey.CRYPTOHALL24_DUERR && (
        <Typography variant='body2'>
          <strong>Credit Card payments:</strong> 3% fee
        </Typography>
      )}
      <Typography variant='body2'>
        <strong>Klarna payment:</strong> 3,5% fee (installments possible)
      </Typography>
    </Box>
  );

  const handleTopUp = handleSubmit(async ({ amount, paymentType }) => {
    let newTab: Window | null = null;

    try {
      newTab = window.open('', '_blank');

      const response = await executePayment({
        amount,
        productId: null,
        ...(paymentType === 'klarna' && { isKlarna: true }),
        paymentType,
      }).unwrap();

      let url: string | undefined;
      if ('RedirectUrl' in response) {
        url = response.RedirectUrl as string;
      } else if ('CheckoutUrl' in response) {
        url = response.CheckoutUrl as string;
      }

      if (url && newTab) {
        newTab.location.href = url;
        dispatch(showSuccessNotification(NOTIFICATIONS.TOP_UP_REQUEST_SUCCESS));
      } else {
        newTab?.close();
        dispatch(showErrorNotification(NOTIFICATIONS.TOP_UP_REQUEST_FAILED));
      }
    } catch (e) {
      newTab?.close();
      const fallbackError = 'Something went wrong creating topup request';
      console.error('Payment failed', e);
      dispatch(showErrorNotification(fallbackError));
    }
  });

  if (!user?.isFullInfoProvided) {
    return <UserInformationMissingModal />;
  }

  return (
    <RootModalLayout onClose={onClose} title='Top up' testId={testId}>
      <section className={classes.content}>
        <Controller
          name='paymentType'
          control={control}
          defaultValue='crypto'
          render={({ field }) => (
            <FormControl component='fieldset'>
              <RadioGroup {...field}>
                {filteredOptions.map(({ value, label, Icon }) => {
                  const adjustedLabel =
                    value === 'fiat' && themeKey === ThemeKey.AMITYAGE ? `${label} (3% Fee)` : label;

                  return (
                    <Box
                      key={value}
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                      p={1.5}
                      border={1}
                      borderRadius={2}
                      mb={1}
                      sx={{
                        borderColor: field.value === value ? 'primary.main' : 'grey.400',
                        backgroundColor: field.value === value ? 'primary.lighter' : 'transparent',
                        cursor: 'pointer',
                        transition: '0.2s',
                      }}
                      onClick={() => field.onChange(value)}
                    >
                      <Box display='flex' alignItems='center' gap={1.5}>
                        <Icon />
                        <Typography fontWeight={500}>{adjustedLabel}</Typography>
                      </Box>
                      <Radio
                        checked={field.value === value}
                        value={value}
                        onChange={() => field.onChange(value)}
                        sx={{ pointerEvents: 'none' }}
                      />
                    </Box>
                  );
                })}
              </RadioGroup>
            </FormControl>
          )}
        />

        <Input
          error={errors.amount?.message}
          placeholder='Amount'
          labelText={`Top up amount ${isUSDCompany ? '$' : '€'}`}
          content='A'
          testId={testId}
          containerClassName={classes.input}
          endIcon={!isUSDCompany ? <EuroIcon /> : <USDIcon />}
          {...register('amount', {
            required: { value: true, message: NOTIFICATIONS.REQUIRED_VALUE },
            pattern: {
              value: POSITIVE_AMOUNT_REGEX,
              message: NOTIFICATIONS.INVALID_VALUE,
            },
            min: { value: 1, message: `${NOTIFICATIONS.MIN_AMOUNT} ${1}` },
          })}
        />

        {themeKey === ThemeKey.CRYPTOHALL24_DUERR || themeKey === ThemeKey.CRYPTOHALL24_QUETTA
          ? additionalPaymentInfo
          : null}
      </section>

      <section className={classes.footer}>
        <Button
          testId={testId}
          color={ButtonColor.SECONDARY}
          onClick={handleTopUp}
          type='submit'
          sx={{ minHeight: 42, minWidth: 100 }}
          disabled={!isValid || isPaymentExecuting}
        >
          {isPaymentExecuting ? <Loader width={25} height={25} inline /> : 'Pay now'}
        </Button>
      </section>
    </RootModalLayout>
  );
};
